import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useEffect, useState } from 'react';
import { Box, Copy, Icon, Image, Input, Link, Optional } from '@honda-canada/design-system-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { compiler } from 'markdown-to-jsx';
import SummaryCardPDF from '../BapSummaryCard/SummaryCardPDF';
import TrimDetails from '../TrimOverview/TrimDetails';
import gtmForGenericCtaLinkClick from '../../core/gtm/gtmForGenericCtaLinkClick';
import themeStyles from './ShareYourBuild.styles';
import usePriceBreakdownGroups from '../PriceBreakdownModal/PriceBreakdownModalContent/usePriceBreakdownGroups';
import useSelectedItems from './hooks/useSelectedItems';
import { ACURA_SITE_NAME } from '../../utils/constants';
import { useAppName, useLanguage } from '../../utils/sitecoreContext';
const VehicleImage = themeStyles.apply(Image, 'VehicleImage');
const ModelBadge = themeStyles.apply(Image, 'ModelBadge');
const BadgeWrapper = themeStyles.apply(Box, 'BadgeWrapper');
const FlexContainer = themeStyles.apply(Box, 'FlexContainer');
const ShareYourBuildCta = themeStyles.apply(Link, 'ShareYourBuildCta');
const CtaWrapper = themeStyles.apply(Box, 'CtaWrapper');
const DescriptionCopy = themeStyles.apply(Copy, 'DescriptionCopy');
const ShareYourBuildContent = ({ description, modelBadgeImage, vehicleImage, modelYear, trimName, transmissionName, ctaText, bigExteriorImage, modelTitle, paymentData, priceLabel, paymentFrequencyLabel, copyButtonText, gtmFields, }) => {
    var _a, _b, _c, _d;
    const language = useLanguage();
    const selectedItems = useSelectedItems();
    const { groups } = usePriceBreakdownGroups();
    const termByMonths = `${(_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _a === void 0 ? void 0 : _a.term} @ ${(_b = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _b === void 0 ? void 0 : _b.apr} %`;
    const appName = useAppName();
    const isVariant = appName === ACURA_SITE_NAME;
    const copyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href);
        gtmForGenericCtaLinkClick({
            ...gtmFields,
            eventName: 'cta_click',
            ctaType: 'click',
            customizeSection: 'share build',
            linkText: 'Copy',
            linkUrl: '',
        });
    };
    const [modelBadgeSvgPaths, setModelBadgeSvgPaths] = useState();
    // workaround to display vehicle badge in pdf file
    useEffect(() => {
        const fetchModelBadge = async () => {
            if (modelBadgeImage === null || modelBadgeImage === void 0 ? void 0 : modelBadgeImage.src) {
                try {
                    const response = await fetch(modelBadgeImage === null || modelBadgeImage === void 0 ? void 0 : modelBadgeImage.src);
                    const text = await response.text();
                    const matches = [...text.matchAll(/<path[^>]+d="([^"]+)"[^>]*>/g)];
                    const extractedSvgPaths = matches.map(match => match[1]);
                    if (extractedSvgPaths === null || extractedSvgPaths === void 0 ? void 0 : extractedSvgPaths.length) {
                        setModelBadgeSvgPaths(extractedSvgPaths);
                    }
                }
                catch (error) {
                    console.error('Error fetching model badge:', error);
                }
            }
        };
        fetchModelBadge();
    }, [modelBadgeImage === null || modelBadgeImage === void 0 ? void 0 : modelBadgeImage.src]);
    return (React.createElement(Box, { "data-testid": "cy-share-your-build-container" },
        React.createElement(FlexContainer, null,
            React.createElement(Optional, { when: bigExteriorImage },
                React.createElement(VehicleImage, { src: bigExteriorImage, alt: vehicleImage === null || vehicleImage === void 0 ? void 0 : vehicleImage.alt, "data-testid": "cy-share-your-build-image" })),
            React.createElement(BadgeWrapper, null,
                React.createElement(ModelBadge, { src: modelBadgeImage === null || modelBadgeImage === void 0 ? void 0 : modelBadgeImage.src, alt: modelBadgeImage === null || modelBadgeImage === void 0 ? void 0 : modelBadgeImage.alt, "data-testid": "cy-share-your-build-namebadge" }),
                React.createElement(Optional, { when: modelYear && trimName && transmissionName },
                    React.createElement(TrimDetails, { modelYear, transmissionName, trimName, align: "flex-start", size: "regular" })))),
        React.createElement(DescriptionCopy, { size: "regular", my: "24px", "data-testid": "cy-share-your-build-description" }, compiler(description)),
        React.createElement(Input, { value: window.location.href, inlineCta: true, ctaText: copyButtonText, ctaEvent: copyToClipboard }),
        React.createElement(CtaWrapper, { style: { marginTop: 32 } },
            React.createElement(PDFDownloadLink, { "data-testid": "cy-share-your-build-download-link", style: { textDecoration: 'none' }, document: React.createElement(SummaryCardPDF, { firstPageData: {
                        image: bigExteriorImage,
                        badgePaths: modelBadgeSvgPaths,
                        modelName: modelTitle,
                        sellingPrice: formatPrice((_c = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _c === void 0 ? void 0 : _c.estimatedBalance, language, 2),
                        price: formatPrice((_d = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _d === void 0 ? void 0 : _d.preferredPaymentAmount, language, 2),
                        termByMonths,
                        priceLabel,
                        paymentFrequencyLabel,
                    }, selectedItems: selectedItems, isVariant: isVariant, groups: groups, language: language }), fileName: "build.pdf", onClick: () => {
                    gtmForGenericCtaLinkClick({
                        ...gtmFields,
                        eventName: 'cta_click',
                        ctaType: 'download',
                        customizeSection: 'share build',
                        linkText: 'Download PDF',
                        linkUrl: '',
                    });
                } }, () => (React.createElement(ShareYourBuildCta, { styling: "tertiary2" },
                React.createElement(Icon, { name: "save", iconColor: "blue" }),
                React.createElement(Box, { "data-testid": "cy-share-your-build-download-text" }, ctaText),
                React.createElement(Icon, { name: "arrowRight", iconColor: "blue" })))))));
};
export default ShareYourBuildContent;
